/**
 * Field-tree operations for the digital assistant input schema builder.
 * Ported from ui-aimanager's `input-schema-configuration/fieldTree.ts`; the
 * tree shape and its pure helpers are unchanged.
 */

export type SchemaFieldType = 'array' | 'boolean' | 'number' | 'object' | 'string';

export interface SchemaField {
  children: SchemaField[];
  collapsed: boolean;
  description: string;
  id: string;
  placeholder: string;
  propertyName: string;
  required: boolean;
  type: SchemaFieldType;
}

function generateFieldId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `field-${crypto.randomUUID()}`;
  }

  return `field-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function createDefaultField(): SchemaField {
  return {
    children: [],
    collapsed: false,
    description: '',
    id: generateFieldId(),
    placeholder: '',
    propertyName: '',
    required: false,
    type: 'string',
  };
}

function cloneFieldWithNewIds(field: SchemaField): SchemaField {
  return {
    ...field,
    children: field.children.map(cloneFieldWithNewIds),
    id: generateFieldId(),
  };
}

function mapTree(
  fields: SchemaField[],
  transform: (field: SchemaField) => SchemaField,
): SchemaField[] {
  return fields.map((field) =>
    transform({ ...field, children: mapTree(field.children, transform) }),
  );
}

export function updateField(
  fields: SchemaField[],
  id: string,
  updater: (field: SchemaField) => SchemaField,
): SchemaField[] {
  return mapTree(fields, (field) => (field.id === id ? updater(field) : field));
}

export function removeField(fields: SchemaField[], id: string): SchemaField[] {
  return fields
    .filter((field) => field.id !== id)
    .map((field) => ({ ...field, children: removeField(field.children, id) }));
}

export function countDescendantFields(field: SchemaField): number {
  return field.children.reduce((total, child) => total + 1 + countDescendantFields(child), 0);
}

export function findField(fields: SchemaField[], id: string): SchemaField | null {
  for (const field of fields) {
    if (field.id === id) {
      return field;
    }

    const found = findField(field.children, id);
    if (found) {
      return found;
    }
  }

  return null;
}

export function addChildField(fields: SchemaField[], parentId: string): SchemaField[] {
  return mapTree(fields, (field) =>
    field.id === parentId
      ? { ...field, children: [...field.children, createDefaultField()] }
      : field,
  );
}

export function duplicateField(fields: SchemaField[], id: string): SchemaField[] {
  return fields.flatMap((field) => {
    if (field.id === id) {
      return [field, cloneFieldWithNewIds(field)];
    }

    return [{ ...field, children: duplicateField(field.children, id) }];
  });
}

export function moveField(
  fields: SchemaField[],
  id: string,
  direction: 'down' | 'up',
): SchemaField[] {
  const index = fields.findIndex((field) => field.id === id);

  if (index === -1) {
    return fields.map((field) => ({
      ...field,
      children: moveField(field.children, id, direction),
    }));
  }

  const targetIndex = direction === 'up' ? index - 1 : index + 1;
  if (targetIndex < 0 || targetIndex >= fields.length) {
    return fields;
  }

  const next = [...fields];
  [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
  return next;
}

export function resetField(fields: SchemaField[], id: string): SchemaField[] {
  return updateField(fields, id, (field) => ({
    ...field,
    children: [],
    description: '',
    placeholder: '',
    propertyName: '',
    required: false,
    type: 'string',
  }));
}

export function changeFieldType(
  fields: SchemaField[],
  id: string,
  type: SchemaFieldType,
): SchemaField[] {
  return updateField(fields, id, (field) => ({
    ...field,
    children: type === 'object' ? field.children : [],
    type,
  }));
}

export interface FlatFieldRow {
  depth: number;
  field: SchemaField;
  kind: 'field';
  numbering: string;
  parentId: null | string;
}

export interface FlatAddChildRow {
  depth: number;
  kind: 'add-child';
  parentField: SchemaField;
}

export type FlatSchemaRow = FlatAddChildRow | FlatFieldRow;

export function flattenVisibleFields(
  fields: SchemaField[],
  depth = 0,
  parentId: null | string = null,
  numberingPrefix = '',
): FlatSchemaRow[] {
  return fields.flatMap((field, index) => {
    const numbering = numberingPrefix ? `${numberingPrefix}.${index + 1}` : `${index + 1}`;
    const row: FlatFieldRow = { depth, field, kind: 'field', numbering, parentId };

    if (field.type === 'object' && !field.collapsed) {
      return [
        row,
        ...flattenVisibleFields(field.children, depth + 1, field.id, numbering),
        { depth: depth + 1, kind: 'add-child', parentField: field } as FlatAddChildRow,
      ];
    }

    return [row];
  });
}
